/**
 * Beacon 2.0 analytics tracker
 *
 * Beacon 2.0 is reference-based: each event sends a small `{ context, data }`
 * envelope to a per-event endpoint, referencing results by `responseId` +
 * `uid`/`parentId` rather than embedding full product mappings.
 *
 * Endpoints:
 *   {origin}/{siteId}/{search|autocomplete|category|recommendations}/{render|impression|clickthrough|addtocart|redirect}
 *
 * Impression/clickthrough use ResultProduct (`type`, uid, parentId, sku).
 * Add-to-cart uses Product (`uid`, parentId, sku, qty, price) — no `type`.
 * Only the recommendations family carries a `data.tag` (the profile).
 * Redirect carries `data.redirect` and is supported by search/autocomplete only.
 *
 * Docs: https://docs.athoscommerce.com/reference/beacon-tracking
 */

import { session } from '@/components/helpers/session'
import { getBeaconOrigin, getAthosSiteID } from '../athos-utils'

export const BEACON_EVENTS = {
  RENDER: 'render',
  IMPRESSION: 'impression',
  CLICKTHROUGH: 'clickthrough',
  ADDTOCART: 'addtocart',
  REDIRECT: 'redirect'
}

const BEACON_INITIATOR = 'athos/shoesnsox-theme/2.0'
const BEACON_CONTENT_TYPE = 'text/plain'
const BEACON_LOG_KEY = 'athosBeaconLog'
const BEACON_LOG_LIMIT = 50
const firedBeaconEvents = new Set()

export function claimBeaconEvent(analytics, responseId, eventType) {
  if (!analytics || !responseId) return false
  const key = `${analytics.type}:${analytics.profile || ''}:${responseId}:${eventType}`
  if (firedBeaconEvents.has(key)) return false
  firedBeaconEvents.add(key)
  return true
}

function clearBeaconState() {
  firedBeaconEvents.clear()
  if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(BEACON_LOG_KEY)
}

function recordBeaconEvent(entry) {
  if (typeof sessionStorage === 'undefined') return
  try {
    const raw = sessionStorage.getItem(BEACON_LOG_KEY)
    const log = raw ? JSON.parse(raw) : []
    log.push({ timestamp: new Date().toISOString(), ...entry })
    if (log.length > BEACON_LOG_LIMIT) log.splice(0, log.length - BEACON_LOG_LIMIT)
    sessionStorage.setItem(BEACON_LOG_KEY, JSON.stringify(log))
  } catch {
    /* ignore */
  }
}

function getBeaconLog() {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(BEACON_LOG_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

if (typeof window !== 'undefined') {
  window.__athosBeacon = {
    table: () =>
      console.table(
        getBeaconLog().map((entry) => ({
          event: entry.eventType,
          status: entry.status ?? (entry.queued != null ? `queued: ${entry.queued}` : ''),
          responseId: entry.payload?.data?.responseId,
          quickView: entry.payload?.data?.quickView,
          results: entry.payload?.data?.results?.map((result) => result.uid || result.parentId).join(', '),
          url: entry.url
        }))
      ),
    json: () => getBeaconLog(),
    clear: () => clearBeaconState()
  }
}

function toBeaconId(value) {
  if (value == null || value === '') return ''
  const text = String(value)
  const gid = text.match(/\/(\d+)\s*$/)
  if (gid) return gid[1]
  const numeric = parseInt(text, 10)
  return Number.isFinite(numeric) ? String(numeric) : text
}

function toBeaconPrice(value) {
  if (value == null || value === '') return null
  const numeric = Number(String(value).replace(/[^0-9.]/g, ''))
  return Number.isFinite(numeric) ? numeric : null
}

function toBeaconSku(value) {
  if (value == null || value === '') return ''
  return String(value)
}

export class AthosBeacon {
  constructor(type = 'recommendations', profile, responseId) {
    this.type = type
    this.profile = profile
    this.responseId = responseId
    this.siteId = getAthosSiteID()
  }

  setResponseId(responseId) {
    this.responseId = responseId
  }

  endpoint(eventType) {
    return `${getBeaconOrigin()}/${this.siteId}/${this.type}/${eventType}`
  }

  generateContext() {
    const shopperId = session.shopper.get()
    const currencyCode = window.GM_STATE?.localization?.currency || window.GM_STATE?.shopify?.defaultCurrency

    return {
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      userId: session.userId.get(),
      sessionId: session.sessionId.get(),
      pageLoadId: session.pageLoadId.get(),
      initiator: BEACON_INITIATOR,
      ...(shopperId && { shopperId }),
      ...(currencyCode && { currency: { code: currencyCode } })
    }
  }

  buildResultReference(result) {
    const uid = toBeaconId(result.uid ?? result.id)
    const parentId = toBeaconId(result.parentId ?? result.groupId ?? result.parent_id ?? uid)
    const sku = toBeaconSku(result.sku)
    return {
      type: 'product',
      uid,
      parentId,
      ...(sku && { sku })
    }
  }

  buildAddToCartLine(result, variant = {}, qty = 1) {
    const uid = toBeaconId(variant.id ?? variant.uid ?? variant.variant_id ?? result?.uid ?? result?.id)
    const parentId = toBeaconId(
      result?.parentId ?? result?.groupId ?? result?.parent_id ?? result?.id ?? uid
    )
    const sku = toBeaconSku(variant.sku ?? result?.sku)
    const price = toBeaconPrice(variant.price ?? result?.price)
    const quantity = Number(qty)

    if (!uid || !parentId || !Number.isFinite(quantity) || price == null) return null

    return {
      uid,
      parentId,
      ...(sku && { sku }),
      qty: quantity,
      price
    }
  }

  send(eventType, data, { keepalive = false, preferFetch = false } = {}) {
    if (!this.siteId || !this.responseId) return

    const payload = {
      context: this.generateContext(),
      data: {
        ...(this.type === 'recommendations' && this.profile && { tag: this.profile }),
        responseId: this.responseId,
        ...data
      }
    }

    const url = this.endpoint(eventType)
    const body = JSON.stringify(payload)

    if (keepalive && !preferFetch && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const queued = navigator.sendBeacon(url, body)
      recordBeaconEvent({ eventType, url, queued, payload })
      return
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': BEACON_CONTENT_TYPE },
      body,
      keepalive
    })
      .then((response) => {
        recordBeaconEvent({ eventType, url, status: response.status, payload })
      })
      .catch(() => {})
  }

  render() {
    return this.send(BEACON_EVENTS.RENDER, {})
  }

  impression(results, { quickView = false } = {}) {
    if (!Array.isArray(results) || results.length === 0) return
    return this.send(BEACON_EVENTS.IMPRESSION, {
      ...(quickView ? { quickView: true } : {}),
      results: results.map((result) => this.buildResultReference(result)),
      banners: []
    })
  }

  clickthrough(result, { quickView = false } = {}) {
    if (!result) return
    return this.send(
      BEACON_EVENTS.CLICKTHROUGH,
      {
        ...(quickView ? { quickView: true } : {}),
        results: [this.buildResultReference(result)]
      },
      { keepalive: true }
    )
  }

  addtocart(result, variant, { qty = 1, quickView = false } = {}) {
    const line = this.buildAddToCartLine(result, variant, qty)
    if (!line) return
    return this.send(
      BEACON_EVENTS.ADDTOCART,
      {
        ...(quickView ? { quickView: true } : {}),
        results: [line]
      },
      { keepalive: true, preferFetch: true }
    )
  }

  // `data.redirect` is required by Athos; sending the envelope without it returns 400.
  redirect(redirectUrl) {
    if (!redirectUrl) return
    return this.send(BEACON_EVENTS.REDIRECT, { redirect: redirectUrl }, { keepalive: true })
  }
}
